import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'

type UseSizeOptions = {
  width?: number,
  height?: number
}
const useSize = (options?: UseSizeOptions) => {
  const observerRef = useRef<ResizeObserver>(undefined)
  const [width, setWidth] = useState(options?.width ?? 0)
  const [height, setHeight] = useState(options?.height ?? 0)
  const ref = useCallback((node: SVGSVGElement | null) => {
    if(observerRef.current){
      observerRef.current.disconnect()
      observerRef.current = undefined
    }
    if(node){
      const listener = () => {
        const {width, height} = node.getBoundingClientRect()
        setWidth(width)
        setHeight(height)
      }
      const observer = observerRef.current = new ResizeObserver(listener)
      observer.observe(node)
      listener()
    }
  }, [])
  return {
    ref,
    width,
    height,
    short: Math.min(width, height),
    long: Math.max(width, height)
  }
}

type SvgProps = {
  size: number,
  strokeWidth?: number
}

export const SvgPlus: FunctionComponent<SvgProps> = (props) => {
  const {ref, short, long} = useSize({width: props.size, height: props.size})
  const size = short
  const strokeWidth = props.strokeWidth ?? 1
  return <svg ref={ref} viewBox={`0 0 ${long} ${long}`}>
    <path d={`M0 ${size / 2}L${size} ${size / 2}M ${size / 2} 0L${size / 2} ${size}`} fill="none" stroke="currentColor" strokeWidth={strokeWidth}/>
  </svg>
}

export const SvgMinus: FunctionComponent<SvgProps> = (props) => {
  const {ref, short, long} = useSize({width: props.size, height: props.size})
  const size = short
  const strokeWidth = props.strokeWidth ?? 1
  return <svg ref={ref} viewBox={`0 0 ${long} ${long}`}>
    <path d={`M0 ${size / 2}L${size - 0} ${size / 2}`} fill="none" stroke="currentColor" strokeWidth={strokeWidth}/>
  </svg>
}

export const SvgCross: FunctionComponent<SvgProps> = (props) => {
  const {ref, short, long} = useSize({width: props.size, height: props.size})
  const size = short
  const strokeWidth = props.strokeWidth ?? 1
  const sin = Math.sin(Math.PI / 4) * size / 2
  return <svg ref={ref} viewBox={`0 0 ${long} ${long}`}>
    <path
      d={`
        M${size / 2 - sin} ${size / 2 - sin}
        L${size / 2 + sin} ${size / 2 + sin}
        M${size / 2 + sin} ${size / 2 - sin}
        L${size / 2 - sin} ${size / 2 + sin}
      `}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
    />
  </svg>
}

export const SvgCircle: FunctionComponent<SvgProps> = (props) => {
  const {ref, short, long} = useSize({width: props.size, height: props.size})
  const size = short
  const strokeWidth = props.strokeWidth ?? 1
  const r = size / 2
  return <svg ref={ref} viewBox={`0 0 ${long} ${long}`}>
    <circle cx={size / 2} cy={size / 2} r={r - strokeWidth / 2} fill="none" stroke="currentColor" strokeWidth={strokeWidth}/>
  </svg>
}

export const SvgLeftAngle: FunctionComponent<SvgProps> = (props) => {
  const {ref, height, long} = useSize({width: props.size, height: props.size})
  const size = height
  const strokeWidth = props.strokeWidth ?? 1
  const tipMargin = strokeWidth / 2 * Math.sqrt(2)
  const endMargin = strokeWidth / 2 / Math.tan(Math.PI / 180 * 45)
  const w = (size / 2 - tipMargin) / Math.tan(Math.PI / 180 * 45)
  return <svg ref={ref} viewBox={`0 0 ${w + tipMargin + endMargin} ${long}`}>
    <path d={`M${w + endMargin} ${endMargin} L${tipMargin} ${size/2}L${w + endMargin} ${size - endMargin}`} fill="none" stroke="currentColor" strokeWidth={strokeWidth}/>
  </svg>
}
export const SvgRightAngle: FunctionComponent<SvgProps> = (props) => {
  const {ref, height, long} = useSize({width: props.size, height: props.size})
  const size = height
  const strokeWidth = props.strokeWidth ?? 1
  const tipMargin = strokeWidth / 2 * Math.sqrt(2)
  const endMargin = strokeWidth / 2 / Math.tan(Math.PI / 180 * 45)
  const w = (size / 2 - tipMargin) / Math.tan(Math.PI / 180 * 45)
  return <svg ref={ref} viewBox={`0 0 ${w + tipMargin + endMargin} ${long}`}>
    <path d={`M${endMargin} ${endMargin} L${w + tipMargin} ${size/2}L${endMargin} ${size - endMargin}`} fill="none" stroke="currentColor" strokeWidth={strokeWidth}/>
  </svg>
}

type SvgRectProps = {
  strokeWidth?: number
}

export const SvgHorizontalRoundedRect: FunctionComponent<SvgRectProps> = (props) => {
  const {ref, width, height, long, short} = useSize()
  const pathRef = useRef<SVGPathElement>(null)
  const [length, setLength] = useState(0)
  const strokeWidth = props.strokeWidth ?? 1
  const strokeMargin = strokeWidth / 2 + 1
  const rectWitdh = width - (strokeMargin * 2)
  const rectHeight = height - (strokeMargin * 2)
  useEffect(() => {
    const path = pathRef.current
    if(path){
      const observer = new ResizeObserver(() => {
        setLength(path.getTotalLength())
      })
      observer.observe(path)
      return () => {
        observer.disconnect()
      }
    }
  }, [])
  return <svg ref={ref} viewBox={`0 0 ${width} ${height}`}>
    <path
      ref={pathRef}
      d={`
        M${strokeMargin + (rectWitdh / 2)} ${strokeMargin}
        L${strokeMargin + rectWitdh - (rectHeight / 2)} ${strokeMargin}
        A${rectHeight / 2} ${rectHeight / 2} 0 0 1 ${strokeMargin + rectWitdh - (rectHeight / 2)} ${strokeMargin + rectHeight}
        L${strokeMargin + (rectHeight / 2)} ${strokeMargin + rectHeight}
        A${rectHeight / 2} ${rectHeight / 2} 0 1 1 ${strokeMargin + (rectHeight / 2)} ${strokeMargin}
        Z
      `}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      style={{'--length': length}}
    />
  </svg>
}